/**
 * 密码验证工具
 * 提供密码强度检查和验证功能
 */

export interface PasswordStrength {
  score: number; // 0-4, 0=弱, 4=强
  level: 'weak' | 'fair' | 'good' | 'strong';
  feedback: string[];
}

/**
 * 检查密码强度
 */
export function checkPasswordStrength(password: string): PasswordStrength {
  const feedback: string[] = [];
  let score = 0;

  // 长度检查
  if (password.length < 8) {
    feedback.push('密码至少需要8个字符');
  } else {
    score += 1;
  }

  // 包含小写字母
  if (!/[a-z]/.test(password)) {
    feedback.push('密码应包含至少一个小写字母');
  } else {
    score += 1;
  }

  // 包含大写字母
  if (!/[A-Z]/.test(password)) {
    feedback.push('密码应包含至少一个大写字母');
  } else {
    score += 1;
  }

  // 包含数字
  if (!/[0-9]/.test(password)) {
    feedback.push('密码应包含至少一个数字');
  } else {
    score += 1;
  }

  // 包含特殊字符
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    feedback.push('密码应包含至少一个特殊字符 (!@#$%^&*等)');
  } else {
    score += 1;
  }

  // 确定强度等级
  let level: 'weak' | 'fair' | 'good' | 'strong';
  if (score <= 2) {
    level = 'weak';
  } else if (score === 3) {
    level = 'fair';
  } else if (score === 4) {
    level = 'good';
  } else {
    level = 'strong';
  }

  return { score, level, feedback };
}

/**
 * 验证密码是否符合要求
 */
export function validatePassword(password: string): { valid: boolean; message?: string } {
  // 最小长度
  if (password.length < 8) {
    return { valid: false, message: '密码至少需要8个字符' };
  }

  // 最大长度
  if (password.length > 128) {
    return { valid: false, message: '密码长度不能超过128个字符' };
  }

  // 必须包含小写字母
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: '密码必须包含至少一个小写字母' };
  }

  // 必须包含大写字母
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: '密码必须包含至少一个大写字母' };
  }

  // 必须包含数字
  if (!/[0-9]/.test(password)) {
    return { valid: false, message: '密码必须包含至少一个数字' };
  }

  // 必须包含特殊字符
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return { valid: false, message: '密码必须包含至少一个特殊字符 (!@#$%^&*等)' };
  }

  return { valid: true };
}

